// ============================================
// Pagination Component - Cursor-based navigation
// ============================================

import React from "react";

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  loading?: boolean;
  className?: string;
}

/**
 * Pagination Component
 *
 * Displays "Showing X-Y of Z results" with prev/next navigation
 * Uses cursor-based pagination (not offset-based)
 */
export function Pagination({
  currentPage,
  totalItems,
  pageSize,
  hasNext,
  hasPrevious,
  onNext,
  onPrevious,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  loading = false,
  className = "",
}: PaginationProps) {
  // Calculate display range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 border-t-2 border-green-700 pt-4 ${className}`}
    >
      {/* Results info */}
      <div className="text-sm text-green-500 tracking-wider">
        {totalItems === 0 ? (
          <span className="text-green-700">NO RESULTS FOUND</span>
        ) : (
          <>
            <span className="text-green-400">SHOWING </span>
            <span className="text-green-300 font-bold">{startItem}</span>
            <span className="text-green-400"> - </span>
            <span className="text-green-300 font-bold">{endItem}</span>
            <span className="text-green-400"> OF </span>
            <span className="text-green-300 font-bold">{totalItems.toLocaleString()}</span>
            <span className="text-green-400"> RESULTS</span>
          </>
        )}
      </div>

      {/* Navigation controls */}
      <div className="flex items-center gap-4">
        {/* Page size selector */}
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-green-600 tracking-wider">PER PAGE:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={loading}
              className="bg-black border border-green-700 text-green-400 px-2 py-1 text-sm focus:border-green-500 focus:outline-none disabled:opacity-50"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Page indicator */}
        {totalPages > 0 && (
          <div className="text-xs text-green-600 tracking-wider">
            PAGE <span className="text-green-400">{currentPage}</span> OF{" "}
            <span className="text-green-400">{totalPages}</span>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={onPrevious}
            disabled={!hasPrevious || loading}
            className={`px-3 py-1 border text-sm font-bold tracking-wider transition-colors ${
              hasPrevious && !loading
                ? "border-green-500 text-green-500 hover:bg-green-500 hover:text-black"
                : "border-green-800 text-green-800 cursor-not-allowed"
            }`}
          >
            {"<< PREV"}
          </button>
          <button
            onClick={onNext}
            disabled={!hasNext || loading}
            className={`px-3 py-1 border text-sm font-bold tracking-wider transition-colors ${
              hasNext && !loading
                ? "border-green-500 text-green-500 hover:bg-green-500 hover:text-black"
                : "border-green-800 text-green-800 cursor-not-allowed"
            }`}
          >
            {"NEXT >>"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Simple pagination for smaller lists
 */
export function SimplePagination({
  hasNext,
  hasPrevious,
  onNext,
  onPrevious,
  loading = false,
  className = "",
}: {
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  loading?: boolean;
  className?: string;
}) {
  return (
    <div className={`flex items-center justify-center gap-4 ${className}`}>
      <button
        onClick={onPrevious}
        disabled={!hasPrevious || loading}
        className={`px-4 py-2 border text-sm font-bold tracking-wider transition-colors ${
          hasPrevious && !loading
            ? "border-green-500 text-green-500 hover:bg-green-500 hover:text-black"
            : "border-green-800 text-green-800 cursor-not-allowed"
        }`}
      >
        [PREVIOUS]
      </button>
      {loading && (
        <span className="text-green-500 animate-pulse text-sm tracking-wider">
          LOADING...
        </span>
      )}
      <button
        onClick={onNext}
        disabled={!hasNext || loading}
        className={`px-4 py-2 border text-sm font-bold tracking-wider transition-colors ${
          hasNext && !loading
            ? "border-green-500 text-green-500 hover:bg-green-500 hover:text-black"
            : "border-green-800 text-green-800 cursor-not-allowed"
        }`}
      >
        [NEXT]
      </button>
    </div>
  );
}

/**
 * Hook to manage pagination state with cursors
 */
export function usePagination(initialPageSize: number = 50) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  const [cursors, setCursors] = React.useState<{
    previous: string | null;
    next: string | null;
  }>({
    previous: null,
    next: null,
  });
  const [totalItems, setTotalItems] = React.useState(0);

  const goToNext = React.useCallback(() => {
    if (cursors.next) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [cursors.next]);

  const goToPrevious = React.useCallback(() => {
    if (cursors.previous) {
      setCurrentPage((prev) => Math.max(1, prev - 1));
    }
  }, [cursors.previous]);

  const reset = React.useCallback(() => {
    setCurrentPage(1);
    setCursors({ previous: null, next: null });
  }, []);

  const updateCursors = React.useCallback(
    (previous: string | null, next: string | null, total?: number) => {
      setCursors({ previous, next });
      if (total !== undefined) {
        setTotalItems(total);
      }
    },
    []
  );

  const changePageSize = React.useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setCursors({ previous: null, next: null });
  }, []);

  return {
    currentPage,
    pageSize,
    cursors,
    totalItems,
    hasNext: !!cursors.next,
    hasPrevious: !!cursors.previous,
    goToNext,
    goToPrevious,
    reset,
    updateCursors,
    setTotalItems,
    changePageSize,
    // Current cursor to use for API call
    currentCursor: currentPage === 1 ? null : cursors.previous ? "previous" : "next",
    cursorValue:
      currentPage === 1
        ? null
        : cursors.previous && currentPage > 1
        ? cursors.previous
        : cursors.next,
  };
}

export default Pagination;
